import * as React from "react";
import emailjs from "emailjs-com";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Box,
  Typography,
  Modal,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Formik, Form } from "formik";
import * as Yup from "yup";

export default function FranchiseForm({ open, setOpen }) {
  const [success, setSuccess] = React.useState(false);
  const [error, setError] = React.useState(false);
  const handleClose = () => setOpen(false);

  const initialValues = {
    name: "",
    email: "",
    phone: "",
    city: "",
    zipcode: "",
    state: "",
    message: "",
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    phone: Yup.string()
      .matches(/^[0-9]{10}$/, "Enter a valid 10-digit phone number")
      .required("Phone is required"),
    city: Yup.string().required("City is required"),
    zipcode: Yup.string(),
    state: Yup.string(),
    message: Yup.string(),
  });

  const sendEmail = async (values, { setSubmitting, resetForm }) => {
    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_FRANCHISE_TEMPLATE_ID,
        {
          name: values.name,
          email: values.email,
          phone: values.phone,
          city: values.city,
          zipcode: values.zipcode,
          state: values.state,
          message: values.message,
          current_year: new Date().getFullYear(),
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );
      setSuccess(true);
      resetForm();
      handleClose();
    } catch (error) {
      console.error("Email sending failed:", error);
      setError(true);
      // alert("Failed to send email. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: { borderRadius: "20px", background: "#fff", overflowX: "hidden" },
        }}
      >
        <DialogTitle
          sx={{ textAlign: "center", fontWeight: "bold", color: "#000", pb: 1 }}
        >
          Be A Franchise Partner
          <Box
            sx={{
              width: 60,
              height: 4,
              backgroundColor: "red",
              mx: "auto",
              mt: 1,
              borderRadius: 2,
            }}
          />
        </DialogTitle>

        <DialogContent dividers sx={{ maxHeight: "80vh", overflowX: "hidden" }}>
          <Typography
            variant="body1"
            align="center"
            sx={{ mb: 3, color: "#333" }}
          >
            Fill in the details below and start your journey with{" "}
            <span style={{ color: "red", fontWeight: "bold" }}>
              Mukhiya Ji Chai
            </span>
          </Typography>

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={sendEmail}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              isSubmitting,
            }) => (
              <Form id="franchise-form">
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2,
                    overflowX: "hidden",
                    pt: "10px",
                  }}
                >
                  <TextField
                    required
                    name="name"
                    label="Full Name"
                    variant="outlined"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.name && !!errors.name}
                    helperText={touched.name && errors.name}
                    fullWidth
                  />
                  <TextField
                    required
                    name="email"
                    label="Email Address"
                    type="email"
                    variant="outlined"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.email && !!errors.email}
                    helperText={touched.email && errors.email}
                    fullWidth
                  />
                  <TextField
                    required
                    name="phone"
                    label="Phone Number"
                    type="tel"
                    variant="outlined"
                    value={values.phone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.phone && !!errors.phone}
                    helperText={touched.phone && errors.phone}
                    fullWidth
                  />
                  <TextField
                    required
                    name="city"
                    label="City"
                    variant="outlined"
                    value={values.city}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.city && !!errors.city}
                    helperText={touched.city && errors.city}
                    fullWidth
                  />
                  <TextField
                    name="zipcode"
                    label="Zipcode"
                    variant="outlined"
                    value={values.zipcode}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    fullWidth
                  />
                  <TextField
                    name="state"
                    label="State"
                    variant="outlined"
                    value={values.state}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    fullWidth
                  />
                  <TextField
                    name="message"
                    label="Message"
                    variant="outlined"
                    multiline
                    rows={3}
                    value={values.message}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    sx={{ gridColumn: { xs: "span 1", sm: "span 2" } }}
                    fullWidth
                  />
                </Box>

                <DialogActions
                  sx={{ px: 3, pb: 3, justifyContent: "center", gap: 2 }}
                >
                  <Button
                    onClick={handleClose}
                    variant="outlined"
                    sx={{
                      borderColor: "#000",
                      color: "#000",
                      fontWeight: 600,
                      "&:hover": { borderColor: "red", color: "red" },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    form="franchise-form"
                    variant="contained"
                    sx={{
                      backgroundColor: "red",
                      color: "#fff",
                      fontWeight: "bold",
                      px: 4,
                      py: 1.2,
                      borderRadius: "10px",
                      boxShadow: "0 4px 14px rgba(255,0,0,0.4)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundColor: "#b30000",
                        transform: "scale(1.05)",
                      },
                    }}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Sending..." : "Submit"}
                  </Button>
                </DialogActions>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>

      {/* Success Popup */}
      <Modal open={success} onClose={() => setSuccess(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "#fff",
            borderRadius: "20px",
            boxShadow: 24,
            p: { xs: 3, sm: 5 },
            textAlign: "center",
            width: { xs: "80%", sm: "400px" },
          }}
        >
          <CheckCircleOutlineIcon
            sx={{ fontSize: 80, color: "green", mb: 2 }}
          />
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Submission Successful!
          </Typography>
          <Typography variant="body1" sx={{ color: "#555", mb: 3 }}>
            Thank you for your interest in{" "}
            <span style={{ color: "red", fontWeight: "bold" }}>
              Mukhiya Ji Chai
            </span>
            . Our team will reach out to you soon.
          </Typography>
          <Button
            onClick={() => setSuccess(false)}
            variant="contained"
            sx={{
              backgroundColor: "red",
              color: "#fff",
              px: 4,
              py: 1,
              borderRadius: "10px",
              "&:hover": { backgroundColor: "#b30000" },
            }}
          >
            Close
          </Button>
        </Box>
      </Modal>
      {/* Error Modal */}
      <Modal open={error} onClose={() => setError(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "#fff",
            borderRadius: "20px",
            boxShadow: 24,
            p: { xs: 3, sm: 5 },
            textAlign: "center",
            width: { xs: "80%", sm: "400px" },
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 80, color: "red", mb: 2 }} />
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Submission Failed
          </Typography>
          <Typography variant="body1" sx={{ color: "#555", mb: 3 }}>
            Oops! Something went wrong while sending your request. <br />
            Please try again later.
          </Typography>
          <Button
            onClick={() => setError(false)}
            variant="contained"
            sx={{
              backgroundColor: "red",
              color: "#fff",
              px: 4,
              py: 1,
              borderRadius: "10px",
              "&:hover": { backgroundColor: "#b30000" },
            }}
          >
            Close
          </Button>
        </Box>
      </Modal>
    </>
  );
}
